import { useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchMetaData, saveMetaData, DEFAULT_META } from '../data/metaApi.js'
import { useHistoryStore } from '../store/historyStore.js'

/**
 * useUpdateMeta — flexible mutation hook for modifying the shared meta object.
 *
 * Callers pass an `updater` function that receives the current meta and
 * returns the new meta. Pattern:
 *   1. onMutate    → apply optimistically to local cache (instant UI)
 *   2. mutationFn  → fetch freshest server state, apply updater to THAT,
 *                    then save. This prevents overwriting updates from other
 *                    writers (e.g. the old /q2-goals.html in another tab).
 *   3. onSuccess   → replace cache with the saved-to-server version so any
 *                    concurrent changes from other writers flow into the UI.
 *   4. onError     → rollback to the pre-optimistic state.
 *
 * Multi-writer safety: without Ably, two browsers can both POST /meta around
 * the same time. "Last writer wins" at the KV level — but by fetching fresh
 * and re-applying our updater, we preserve other writers' fields and only
 * overwrite the fields we explicitly changed. It's a defensive merge, not a
 * true CRDT, but it's good enough for this scale.
 *
 * Usage example:
 *   const updateMeta = useUpdateMeta()
 *   updateMeta.mutate((m) => ({
 *     ...m,
 *     weeklySummary: {
 *       ...m.weeklySummary,
 *       'p1-g0': { tldr: 'New TLDR', wins: '…', flags: '…', savedBy: 'Ricky', savedAt: Date.now() },
 *     },
 *   }))
 */
export function useUpdateMeta() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (updater) => {
      // Fetch the freshest server state — may include updates from other
      // writers since our local cache was last refreshed.
      const fresh = await fetchMetaData()
      // Apply OUR change to the fresh state, not the stale local cache.
      // This means other writers' concurrent changes are preserved;
      // we only overwrite the fields we're actually changing.
      const next = updater(fresh)
      await saveMetaData(next)
      return next
    },

    // Optimistic: apply to local cache for instant UI feedback.
    onMutate: async (updater) => {
      await queryClient.cancelQueries({ queryKey: ['meta'] })
      const previous = queryClient.getQueryData(['meta']) || DEFAULT_META

      // Push a snapshot to the undo stack (also capture goals so undo can
      // restore both layers if the user's action touched both)
      useHistoryStore
        .getState()
        .pushSnapshot('Edit', queryClient.getQueryData(['goals']), previous)

      queryClient.setQueryData(['meta'], updater(previous))
      return { previous }
    },

    onSuccess: (next) => {
      // Sync cache with what actually got saved — this may include fields
      // from other writers that weren't in our optimistic cache.
      queryClient.setQueryData(['meta'], next)
    },

    onError: (err, vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['meta'], ctx.previous)
    },
  })
}

// Previously we exported `useUpdateMetaLocal` for the presence heartbeat.
// That variant is now unnecessary — the heartbeat writes directly to a
// dedicated `/meta/presence` partial-update endpoint on the worker, which
// does a server-side merge and cannot clobber any other meta field.
